import { transformPoint, type Frame, type MeshBuilder } from '../mesh/meshBuilder';

/* A part drawn in `inner` inside `outer`: the two turns about the vertical add, and the inner
   origin is carried by the outer frame. */
function compose(outer: Frame, inner: Frame): Frame {
  return { origin: transformPoint(inner.origin, outer), yaw: outer.yaw + inner.yaw };
}

/**
 * A builder that draws into `target` everything given it in its own frame, placed by `frame`: a
 * hull built in its ship's frame goes straight into a world chunk. Ground polygons lie in the
 * layout and have no frame to be carried by, so it refuses them.
 */
export function framedBuilder(target: MeshBuilder, frame: Frame): MeshBuilder {
  return {
    quad: (style, inner, corners, uv) => {
      target.quad(style, compose(frame, inner), corners, uv);
    },
    polygon: (style, inner, outline, uvOf) => {
      target.polygon(style, compose(frame, inner), outline, uvOf);
    },
    box: (style, inner, min, max, faces) => {
      target.box(style, compose(frame, inner), min, max, faces);
    },
    prism: (style, inner, base, y0, y1, faces) => {
      target.prism(style, compose(frame, inner), base, y0, y1, faces);
    },
    cylinder: (style, inner, base, radiusBottom, radiusTop, height, segments, faces) => {
      target.cylinder(
        style,
        compose(frame, inner),
        base,
        radiusBottom,
        radiusTop,
        height,
        segments,
        faces
      );
    },
    groundPolygon: () => {
      throw new Error('A ground polygon lies in the layout and cannot be drawn in a frame');
    },
    get vertexCount() {
      return target.vertexCount;
    },
    get triangleCount() {
      return target.triangleCount;
    },
    build: () => target.build(),
    finish: () => target.finish(),
  };
}
